import * as Actions from '../actions/courtDeadlineRuleActions';
import CourtDeadlineRule from '../../entities/courtDeadlineRule';
import * as idb from '../../lib/idbd';
import locals from '../../lib/localStorage';
import ArrayMap from '../../lib/ArrayMap';
import ArrayMapRegistry from '../../lib/ArrayMapRegistry';
import {ensure} from '../../lib/picos/ensure';
import {isRecoverableError} from '../../lib/recoverableErrors';

export type CourtDeadlineRuleAction = { 
    type: string; 
    courtDeadlineRule: CourtDeadlineRule | null; 
    courtDeadlineRuleId: string | CourtDeadlineRule; 
    partial: boolean; 
    lastFetch: number; 
    courtDeadlineRules: CourtDeadlineRule[]; 
    err: any; 
    status: number; 
    message: string; 
}

let logging: boolean = locals.getBoolean(locals.REDUX_LOGGING, false);

/**
 * Call the storage worker.
 *
 * @param {ArrayMap<CourtDeadlineRule> | null} data
 * @param {string} firmId
 * @param {string} userId
 * @param {CourtDeadlineRule[]} [updates]
 */
const callStorageWorker = (data: ArrayMap<CourtDeadlineRule> | null, firmId: string, userId: string, updates?: CourtDeadlineRule[]) => {
	window.storageWorker
		  .updateStorage(firmId, userId, 'courtDeadlineRule', 'courtDeadlineRules', data?.values() || null, updates)
		  .catch(console.error);
}

/**
 * Update storage in backend.
 *
 * @param {ArrayMap<CourtDeadlineRule>} data
 * @param {CourtDeadlineRule[]} [updates]
 * @returns {ArrayMap<CourtDeadlineRule>}
 */
function updateStorage(data: ArrayMap<CourtDeadlineRule>, updates?: CourtDeadlineRule[]): ArrayMap<CourtDeadlineRule> {
	if (logging) console.debug('⮂ courtDeadlineRuleReducer: updateStorage');
	
	const firmId = locals.getString(locals.USER_FIRM_ID, '');
	const userId = locals.getString(locals.USER_ID, '');

	if (updates && updates.length === 0) return data;
	else if (updates) callStorageWorker(null, firmId, userId, updates);
	else callStorageWorker(data, firmId, userId);

	return data;
}

/**
 * Update storage in backend.
 *
 * @param {CourtDeadlineRule[]} [updates]
 * @returns {ArrayMap<CourtDeadlineRule>}
 */
function updateStorageNoReturn(updates?: CourtDeadlineRule[]): void {
	if (logging) console.debug('⮂ courtDeadlineRuleReducer: updateStorageNoReturn');
	
	const firmId = locals.getString(locals.USER_FIRM_ID, '');
	const userId = locals.getString(locals.USER_ID, '');

	if (updates && updates.length > 0) callStorageWorker(null, firmId, userId, updates);
}

/**
 * Create state with optional data.
 *
 * @param {CourtDeadlineRule[]} [data]
 * @returns {ArrayMap<CourtDeadlineRule>}
 */
function createState(data?: CourtDeadlineRule[] | ArrayMap<CourtDeadlineRule>): ArrayMap<CourtDeadlineRule> {
	if (!data) return ArrayMapRegistry.clone<CourtDeadlineRule>('courtDeadlineRule', []);
    else if (data instanceof ArrayMap) return ArrayMapRegistry.clone<CourtDeadlineRule>('courtDeadlineRule', data.map(e => e));
    else return ArrayMapRegistry.clone<CourtDeadlineRule>('courtDeadlineRule', data);
}

function courtDeadlineRules(state: ArrayMap<CourtDeadlineRule>, action: CourtDeadlineRuleAction): ArrayMap<CourtDeadlineRule> {
	if (!state) state = createState();

	let st: ArrayMap<CourtDeadlineRule>;

	switch (action.type) {
		// action.courtDeadlineRule - the new value of courtDeadlineRule with id action.id
		// action.courtDeadlineRuleId - id of the target, used if new value is null
		case Actions.COURTDEADLINERULE_FETCH_ONE_SUCCESS:
			/* TODO: This is busted for all but modifications! */
			if (logging) console.debug('⮂ courtDeadlineRuleReducer: FETCH_ONE_SUCCESS', action);

			if (action.courtDeadlineRule === null) return state;

			// The addedOn date of the existing one will always be greater than the one passed through
			// (Because it was updated!)
			if (state.has(action.courtDeadlineRuleId)) {
				st = state.clone();
				st.set(action.courtDeadlineRule);
				// state.clear();

				updateStorageNoReturn([action.courtDeadlineRule]);
				return st;
			}
			else return state;

		/* Handle incoming fetch:
			 partial, stateEmpty, fetchEmpty		-> A do nothing, return state
			 partial, stateEmpty, !fetchEmpty	 -> B return action.courtDeadlineRules
			 partial, !stateEmpty, fetchEmpty	 -> A do nothing, return state
			 partial, !stateEmpty, !fetchEmpty	-> C update/add entries in action.courtDeadlineRules
			 !partial, stateEmpty, fetchEmpty	 -> A do nothing, return state
			 !partial, stateEmpty, !fetchEmpty	-> B return action.courtDeadlineRules
			 !partial, !stateEmpty, fetchEmpty	-> D return courtDeadlineRules in state newer than last fetch time
			 !partial, !stateEmpty, !fetchEmpty -> E merge courtDeadlineRules in state newer than those in action.courtDeadlineRules
		 */
		case Actions.COURTDEADLINERULE_FETCH_SUCCESS:
			const partial = action.partial;
			const lastFetch = action.lastFetch;
			const stateEmpty = state.length === 0;
			const fetchEmpty = action.courtDeadlineRules.length === 0;
			const fetched = action.courtDeadlineRules.map(courtDeadlineRule => new CourtDeadlineRule({...courtDeadlineRule, persisted: true}));

			if (logging) console.debug(`⮂ courtDeadlineRuleReducer: ${partial ? 'partial' : 'full'} fetch of ${fetched.length} courtDeadlineRules with ${stateEmpty ? 'empty' : 'non-empty'} state and ${fetchEmpty ? 'empty' : 'non-empty'} fetch`);

			// ✓ We didn't have anything in our state, so whatever is in fetched is the new state
			const replaceState = () => {
				if (logging) console.debug('⮂ courtDeadlineRuleReducer: replaceState', {partial, stateEmpty, fetchEmpty, lastFetch});

				if (fetched.length) {
					// Create a new state based on the fetched results.
					st = createState(fetched);

					return updateStorage(st);
				}
				else return state;
			}

			// ✓ Partial fetch with result and state was not empty, update state with fetched result.
			//	 Bear in mind that partial fetches includes deletes for all entities, so we need to use that
			//	 information to delete from current state if status of the fetched result is 'D'.
			const partialMerge = () => {
				if (logging) console.debug('⮂ courtDeadlineRuleReducer: partialMerge', {partial, stateEmpty, fetchEmpty, lastFetch});

				// Use a fresh copy of the state
				const st: ArrayMap<CourtDeadlineRule> = state.clone();

				// We only updateStorage if there is something to update
				const toUpdate: CourtDeadlineRule[] = [];

				// For each item, add it if it doesn't exist, replace if it does, and check for deleted
				fetched.forEach(courtDeadlineRule => {
					const current = st.get(courtDeadlineRule.id);

					if (current) {
						/* Has it been deleted?
						 -- Note that partials include deleted for everything!
						 -- Note that some tables retain the deleted, and some do not.
						 -- We need to do the right thing here
						*/
						if (current.status !== 'D' && courtDeadlineRule.status === 'D') {
							// Remove it from state
							toUpdate.push(courtDeadlineRule);
							st.delete(courtDeadlineRule);
						}

						/* Not deleted */
						else {
							// Is it newer than what we have? Update the backups and set it locally
							if (courtDeadlineRule.addedOn > current.addedOn) {
								toUpdate.push(courtDeadlineRule);
								st.set(courtDeadlineRule);
							}
						}
					}

					// Otherwise add it (but not if it's deleted — no point adding a deleted entry we never had)
					else {
						if (courtDeadlineRule.status !== 'D') {
							toUpdate.push(courtDeadlineRule);
							st.set(courtDeadlineRule);
						}
						else if (logging) console.debug('⮂ courtDeadlineRuleReducer: partialMerge: skipping deleted courtDeadlineRule not in state');
					}
				});

				return updateStorage(st, toUpdate);
			};

			// ✓ Full dataset with no data returned but existing state: return empty set.
			const empty = () => {
				if (logging) console.debug('⮂ courtDeadlineRuleReducer: empty: courtDeadlineRules', {partial, stateEmpty, fetchEmpty, lastFetch});

				// Clone and clear to produce an empty state with the same schema
				st = state.clone();
				st.clear();

				return updateStorage(st);
			};

			// ✓ Full dataset returned with entries but existing state, must merge.
			//	 The lastFetch variable will have the time (hrt) we started the fetch, which we can use to determine
			//	 which to use, state or fetched.
			//	 For each fetched result, we need to find the matching entry in state.
			//	 Then we need to double-check that the state has not been updated since the last save.
			const fullMerge = () => {
				if (logging) console.debug('⮂ courtDeadlineRuleReducer: fullMerge: courtDeadlineRules', {partial, stateEmpty, fetchEmpty, lastFetch});

				st = createState(fetched); // Start with the new stuff

				// Fetched represents the entire dataset, so we just need to update the fetched results with anything not yet saved.
                state.forEach(localCourtDeadlineRule => {
                    // If this courtDeadlineRule has not yet been saved, the remote state may not know about it.
                    // BUT the persisted flag is unreliable (batch saves stamp it false), so a stale
                    // !persisted local must NOT shadow a newer authoritative server row (OWA-7 #1).
                    // Only re-apply a !persisted local when it is absent from the fetched set OR is
                    // strictly newer by addedOn than the fetched row for the same id. Otherwise the
                    // fetched (server) row wins.
                    if (!localCourtDeadlineRule.persisted) {
                        const fetchedCourtDeadlineRule = st.get(localCourtDeadlineRule.id);
                        if (!fetchedCourtDeadlineRule || localCourtDeadlineRule.addedOn > fetchedCourtDeadlineRule.addedOn) st.set(localCourtDeadlineRule);
                    }
                });

				return updateStorage(st);
			};

			if (partial) {										// ✓ Fetched results are only things changed since last check
				if (stateEmpty) {								// ✓ Partial fetch with nothing in state
					if (fetchEmpty) return state; 	// ✓ Partial fetch with no results and nothing in state just return state
					else return replaceState();			// ✓ Partial fetch with some results, but nothing in state, so this is the new state
				}
				else {											// ✓ There are existing entries in state
					if (fetchEmpty) return state;				// ✓ Partial fetch with no results, nothing to do but return state
					else return partialMerge(); 	// ✓ Partial fetch with result and state was not empty, update state with fetched result
				}
			}
			else {												// ✓ Full dataset returned
				if (stateEmpty) {								// ✓ We are getting back the full result set
					if (fetchEmpty) return state; 	// ✓ Full dataset with no data, but state was empty anyway, just return state
					else return replaceState();			// ✓ Full dataset returned with data, so this is new state
				}
				else {											// ✓ Full dataset returned but there is some data in state
					if (fetchEmpty) return empty();				// ✓ Full dataset with no data returned but existing state: return empty set.
					else return fullMerge();		// ✓ Full dataset returned with entries but existing state, must merge
				}
			}


		case Actions.COURTDEADLINERULE_FETCH_ONE_FAILURE:
			console.error('COURTDEADLINERULE_FETCH_ONE_FAILURE', action.courtDeadlineRuleId, action.err);

			// No update to storage required
			return state;

		case Actions.COURTDEADLINERULE_FETCH_FAILURE:
			console.error('COURTDEADLINERULE_FETCH_FAILURE', action.courtDeadlineRuleId, action.err);

			// No update to storage required
			return state;

		case Actions.COURTDEADLINERULE_RESTORE: {
		    let courtDeadlineRule = new CourtDeadlineRule({...action.courtDeadlineRule});
            st = state.clone();

            const existing = st.get(courtDeadlineRule.id);

            if (existing) {
                // Only update if the incoming version is at least as new
                if (courtDeadlineRule.addedOn >= existing.addedOn) {
                    st.set(courtDeadlineRule);
                    return updateStorage(st, [courtDeadlineRule]);
                }
                else return state;
            }
            else {
                // Don't add deleted entries that aren't already in state
                if (courtDeadlineRule.status === 'D') return state;

                st.set(courtDeadlineRule);
                return updateStorage(st, [courtDeadlineRule]);
            }
        }
            
		case Actions.COURTDEADLINERULE_SAVE: {
		    // console.debug('⮂ COURTDEADLINERULE_SAVE reducer', action.courtDeadlineRule?.id);
		    
			// clone the courtDeadlineRule
			let courtDeadlineRule = new CourtDeadlineRule({...action.courtDeadlineRule});

			st = state.clone();
			// state.clear(); 
			
			
			// Add or update the new courtDeadlineRule in the state
			// If this is a create, add to the queue
			const _courtDeadlineRule = st.get(courtDeadlineRule.id);

			if (!_courtDeadlineRule) st.set(courtDeadlineRule);
			else {
				if (courtDeadlineRule.addedOn >= _courtDeadlineRule.addedOn) st.set(courtDeadlineRule);
			}

			return updateStorage(st, [courtDeadlineRule]);
		}

		case Actions.COURTDEADLINERULE_SAVE_MANY: {
		    if (logging) console.debug('⮂ COURTDEADLINERULE_SAVE_MANY reducer', action.courtDeadlineRules.map(courtDeadlineRule => courtDeadlineRule.id).join(','));
		    
			// clone the courtDeadlineRules
			let toProcess = action.courtDeadlineRules.map(courtDeadlineRule => new CourtDeadlineRule(courtDeadlineRule));

			st = state.clone();
			// state.clear();

			const updates: CourtDeadlineRule[] = [];

			toProcess.forEach(courtDeadlineRule => {
				// Add or update the new courtDeadlineRule in the state
				// If this is a create, add to the queue
				const existing = st.get(courtDeadlineRule.id);

				if (!existing) {
					// Don't add deleted entries that aren't already in state
					if (courtDeadlineRule.status !== 'D') {
						st.push(courtDeadlineRule);
						updates.push(courtDeadlineRule);
					}
					else if (logging) console.debug('⮂ COURTDEADLINERULE_SAVE_MANY: skipping deleted courtDeadlineRule not in state');
				}
				else {
					if (courtDeadlineRule.addedOn >= existing.addedOn) {
						st.set(courtDeadlineRule);
						updates.push(courtDeadlineRule);
					}
					else if (logging) console.debug('⮂ Skipping, existing courtDeadlineRule had newer date.')
				}
			});

			return updateStorage(st, updates);
		}

		case Actions.COURTDEADLINERULE_REMOVE: {
			// clone the courtDeadlineRule
			const courtDeadlineRule = new CourtDeadlineRule({...action.courtDeadlineRule});

			// Remove from UI
			st = state.clone();
			// state.clear();

			st.delete(courtDeadlineRule);

			return updateStorage(st, [courtDeadlineRule]);
		}

		case Actions.COURTDEADLINERULE_RESTORE_FAILURE:
		case Actions.COURTDEADLINERULE_SAVE_FAILURE: {
		    console.warn('COURTDEADLINERULE_SAVE_FAILURE reducer', action.courtDeadlineRule?.id);
		    if (!action.courtDeadlineRule?.id) {
				console.warn('COURTDEADLINERULE_SAVE_FAILURE reducer', action.err);
				return state;
		    }
			
			const courtDeadlineRule = ensure(CourtDeadlineRule, action.courtDeadlineRule);
			if (!courtDeadlineRule) {
				console.error('Could not find courtDeadlineRule after call to save');
				return state;
			}

			st = state.clone();

			const _courtDeadlineRuleRef = state.get(courtDeadlineRule.id);

			if (_courtDeadlineRuleRef && (_courtDeadlineRuleRef.addedOn === courtDeadlineRule.addedOn)) {
				// Create a new instance to avoid mutating the entity shared with old state
				const _courtDeadlineRule = new CourtDeadlineRule({..._courtDeadlineRuleRef});
				_courtDeadlineRule.fatalError = {
					status: action.status,
					message: action.message,
					recoverable: isRecoverableError(action.status, action.message)
				};
				_courtDeadlineRule.persisted = true;	// Not really persisted, but can't keep it blue

				// Update the queue with the new info, so that we can filter these out in the queues
				idb.add('courtDeadlineRule', _courtDeadlineRule).then(r => {
						if (r.isErr()) console.error(`⮂ courtDeadlineRuleReducer: failed to save courtDeadlineRule with fatalError info: ${r.unwrapErr().message}`);
						else console.debug('⮂ courtDeadlineRuleReducer: saved courtDeadlineRule with fatalError info');
					}).catch(err => console.error(`⮂ courtDeadlineRuleReducer: failed to save courtDeadlineRule with fatalError info: ${err.message}`));

				st.set(_courtDeadlineRule);
			}

			return updateStorage(st);
		}

		case Actions.COURTDEADLINERULE_SAVE_MANY_FAILURE: {
		    console.warn('COURTDEADLINERULE_SAVE_MANY_FAILURE reducer', action.courtDeadlineRules.map(courtDeadlineRule => courtDeadlineRule.id).join(','));
		    
			// NOT IMPLEMENTED
			return state;
		}

		case Actions.COURTDEADLINERULE_RESTORE_SUCCESS:
		case Actions.COURTDEADLINERULE_SAVE_SUCCESS: {
			if (!action.courtDeadlineRule?.id) {
				console.warn('COURTDEADLINERULE_SAVE_SUCCESS reducer');
                return state;
			}

			const courtDeadlineRuleRef = state.get(action.courtDeadlineRule?.id);
			if (!courtDeadlineRuleRef) {
				console.warn('Could not find courtDeadlineRule after call to save');
                return state;
			}

			st = state.clone();

			if (action.courtDeadlineRule.addedOn >= courtDeadlineRuleRef.addedOn) {
				st.set(action.courtDeadlineRule);
			}
			else {
				// Create a new instance to avoid mutating the entity shared with old state
				const courtDeadlineRule = new CourtDeadlineRule({...courtDeadlineRuleRef, persisted: true});
				st.set(courtDeadlineRule);
			}

			return updateStorage(st, [action.courtDeadlineRule]);
		}

		case Actions.COURTDEADLINERULE_SAVE_MANY_SUCCESS: {
		    // console.debug('⮂ COURTDEADLINERULE_SAVE_MANY_SUCCESS reducer', action.courtDeadlineRules.map(courtDeadlineRule => courtDeadlineRule.id).join(','));

			// We need to update all of the saved items, then push the items to updateStorage
			st = state.clone();

			const courtDeadlineRules: CourtDeadlineRule[] = [];

			action.courtDeadlineRules.forEach(e => {
				const courtDeadlineRuleRef = st.get(e.id);

                // If we can't find it, it was probably deleted
				if (!courtDeadlineRuleRef) console.warn('Could not find courtDeadlineRule after call to save');
				else if (e.addedOn >= courtDeadlineRuleRef.addedOn) {
				    st.set(e);
				    courtDeadlineRules.push(e);
                }
				else {
					// Create a new instance to avoid mutating the entity shared with old state
					const courtDeadlineRule = new CourtDeadlineRule({...courtDeadlineRuleRef, persisted: true});
					st.set(courtDeadlineRule);
					courtDeadlineRules.push(courtDeadlineRule);
				}
			});

			return updateStorage(st, courtDeadlineRules);
		}

		case Actions.COURTDEADLINERULE_REMOVE_FAILURE: {
		    console.warn('COURTDEADLINERULE_REMOVE_FAILURE action', action.courtDeadlineRule?.id);
		    
			// No storage update needed
			return state;
		}

		case Actions.COURTDEADLINERULE_REMOVE_SUCCESS: {
			// No storage update needed
			return state;
		}

		case Actions.COURTDEADLINERULE_RESTORE_MANY: {
			if (logging) console.debug('⮂ COURTDEADLINERULE_RESTORE_MANY reducer', action.courtDeadlineRules.map(courtDeadlineRule => courtDeadlineRule.id).join(','));

			const toProcess = action.courtDeadlineRules.map(courtDeadlineRule => new CourtDeadlineRule({...courtDeadlineRule}));
			st = state.clone();

			const updates: CourtDeadlineRule[] = [];

			toProcess.forEach(courtDeadlineRule => {
				const existing = st.get(courtDeadlineRule.id);

				if (existing) {
					// Only update if the incoming version is at least as new
					if (courtDeadlineRule.addedOn >= existing.addedOn) {
						st.set(courtDeadlineRule);
						updates.push(courtDeadlineRule);
					}
					else if (logging) console.debug('⮂ COURTDEADLINERULE_RESTORE_MANY: skipping older version of courtDeadlineRule');
				}
				else {
					// Don't add deleted entries that aren't already in state
					if (courtDeadlineRule.status !== 'D') {
						st.set(courtDeadlineRule);
						updates.push(courtDeadlineRule);
					}
					else if (logging) console.debug('⮂ COURTDEADLINERULE_RESTORE_MANY: skipping deleted courtDeadlineRule not in state');
				}
			});

			return updateStorage(st, updates);
		}

		case Actions.COURTDEADLINERULE_REMOVE_MANY: {
			if (logging) console.debug('⮂ COURTDEADLINERULE_REMOVE_MANY reducer', action.courtDeadlineRules.map(courtDeadlineRule => courtDeadlineRule.id).join(','));

			const toProcess = action.courtDeadlineRules.map(courtDeadlineRule => new CourtDeadlineRule({...courtDeadlineRule}));
			st = state.clone();

			toProcess.forEach(courtDeadlineRule => {
				st.delete(courtDeadlineRule);
			});

			return updateStorage(st, toProcess);
		}

		default:
			// No storage update needed
			return state;
	}
}

export default courtDeadlineRules;